import React, { useState, useEffect, useMemo } from "react";
import { Table, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { TableProps, ColumnsType } from "antd/es/table";
import "antd/dist/reset.css";
import CommonSelect from "../../../../../../core/common/commonSelect";
import { formatNumber } from "../../../helpers/numbers.helper";

import "../../../styles/charts/LeadSourcesDataTable.scss";

interface SourceStageData {
    source: string;
    stages: Record<string, number>;
    total: number;
    children?: SourceStageData[];
    key?: string;
}

interface SourcesStageWiseDataTableProps {
    data: any;
    loading?: boolean;
    error?: string;
    showPagination?: boolean;
    pageSize?: number;
    showSearch?: boolean;
    maxHeight?: number;
}

const isLightColor = (hexColor: string): boolean => {
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 2), 16);
    const b = parseInt(hex.substring(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.7;
};


const getContrastingColors = (baseColor: string) => {
    const isLight = isLightColor(baseColor);

    if (isLight) {

        return {
            backgroundColor: `${baseColor}80`,
            color: "#1f2937",
            border: `1px solid ${baseColor}`,
        };
    } else {
        return {
            backgroundColor: `${baseColor}20`,
            color: baseColor,
            border: `1px solid ${baseColor}40`,
        };
    }
};

const SourcesStageWiseDataTable: React.FC<SourcesStageWiseDataTableProps> = ({
    data,
    loading = false,
    error,
    showPagination = true,
    pageSize = 4,
    showSearch = true,
    maxHeight = 280,
}) => {
    const [searchText, setSearchText] = useState("");
    const [filteredData, setFilteredData] = useState<SourceStageData[]>([]);
    const [selectedView, setSelectedView] = useState<string>("total");
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

    const currentData = useMemo(() => {
        if (selectedView === "total") {
            return data?.total || [];
        }
        const formData = data?.by_form?.find((form: any) => form.form_id.toString() === selectedView);
        return formData?.sources || [];
    }, [data, selectedView]);

    const uniqueStages = useMemo(() => {
        const stagesSet = new Set<string>();

        const extractStages = (items: SourceStageData[]) => {
            items.forEach((item) => {
                if (item.stages) {
                    Object.keys(item.stages).forEach((stage) => stagesSet.add(stage));
                }
                if (item.children && item.children.length > 0) {
                    extractStages(item.children);
                }
            });
        };

        extractStages(currentData);
        return Array.from(stagesSet);
    }, [currentData]);

    const getStageColor = (stageName: string) => {
        if (!data?.pipelines || data.pipelines.length === 0) return "#6366f1";

        const pipeline = data.pipelines[0];
        const stageIndex = pipeline.stages?.indexOf(stageName);

        if (stageIndex !== -1 && pipeline.stages_color) {
            return pipeline.stages_color[stageIndex];
        }
        return "#6366f1";
    };

    const addKeysToData = (
        dataArray: SourceStageData[],
        parentKey: string = ""
    ): SourceStageData[] => {
        return dataArray.map((item, index) => {
            const key = parentKey ? `${parentKey}-${index}` : `${index}`;
            const processedItem = {
                ...item,
                key,
            };

            if (item.children && item.children.length > 0) {
                processedItem.children = addKeysToData(item.children, key);
            }

            return processedItem;
        });
    };

    const processedData = useMemo(() => {
        return addKeysToData(currentData);
    }, [currentData]);

    const searchInData = (
        dataArray: SourceStageData[] | any,
        searchValue: string
    ): SourceStageData[] => {
        const searchLower = searchValue.toLowerCase();
        return dataArray
            .map((item: any) => {
                const matches = item.source?.toLowerCase().includes(searchLower);

                let filteredChildren: SourceStageData[] | any;
                if (item.children && item.children.length > 0) {
                    filteredChildren = searchInData(item.children, searchValue);
                }

                if (matches || (filteredChildren && filteredChildren.length > 0)) {
                    return {
                        ...item,
                        children:
                            filteredChildren && filteredChildren.length > 0
                                ? filteredChildren
                                : item.children,
                    };
                }

                return null;
            })
            .filter((item: any) => item !== null);
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        if (!value) {
            setFilteredData(processedData);
            setExpandedRowKeys([]);
            return;
        }

        const filtered = searchInData(processedData, value);
        setFilteredData(filtered);

        const getAllKeys = (dataArray: SourceStageData[]): string[] => {
            const keys: string[] = [];
            dataArray.forEach((item) => {
                if (item.children && item.children.length > 0) {
                    keys.push(item.key!);
                    keys.push(...getAllKeys(item.children));
                }
            });
            return keys;
        };
        setExpandedRowKeys(getAllKeys(filtered));
    };

    useEffect(() => {
        setFilteredData(processedData);
        setSearchText("");
        setExpandedRowKeys([]);
    }, [processedData]);

    if (error) {
        return (
            <div className="table-error-state">
                <i className="ti ti-alert-circle text-danger" />
                <p className="text-danger">Error loading data</p>
                <small className="text-muted">{error}</small>
            </div>
        );
    }

    const columns: ColumnsType<SourceStageData> = [
        {
            title: "Source",
            dataIndex: "source",
            key: "source",
            render: (text: string) => (
                <div className="d-flex align-items-center">
                    <h6 className="fs-13 fw-medium mb-0">{text}</h6>
                </div>
            ),
            sorter: (a, b) => (a.source || "").localeCompare(b.source || ""),
        },
        ...uniqueStages.map((stage) => ({
            title: stage,
            dataIndex: ["stages", stage],
            key: `stage_${stage}`,
            render: (value: number) => {
                const baseColor = getStageColor(stage);
                const { backgroundColor, color, border } = getContrastingColors(baseColor);

                return (
                    <h6
                        className="badge"
                        style={{
                            backgroundColor,
                            color,
                            border,
                            fontWeight: 500,
                        }}
                    >
                        {formatNumber(value || 0)}
                    </h6>
                );
            },
            sorter: (a: any, b: any) => (a.stages?.[stage] || 0) - (b.stages?.[stage] || 0),
            align: "center" as const,
            width: 100,
        })),
        {
            title: "Total",
            dataIndex: "total",
            key: "total",
            render: (text: number) => (
                <h6 className="badge badge-primary">{formatNumber(text)}</h6>
            ),
            sorter: (a, b) => a.total - b.total,
            align: "center",
            width: 100,
        },
    ];

    const viewOptions = [
        { value: "total", label: "All Sources (Total)" },
        ...(data?.by_form?.map((form: any) => ({
            value: form.form_id.toString(),
            label: `${form.form_name} (${form.pipeline_name})`,
        })) || []),
    ];

    const selectedOption = viewOptions.find(opt => opt.value === selectedView) || viewOptions[0];

    const tableProps: TableProps<SourceStageData> = {
        columns,
        dataSource: filteredData,
        loading,
        rowKey: "key",
        scroll: {
            y: maxHeight,
            x: "max-content",
        },
        pagination: showPagination
            ? {
                pageSize,
                showSizeChanger: false,
                showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
            }
            : false,
        expandable: {
            expandedRowKeys,
            onExpandedRowsChange: (keys: any) => setExpandedRowKeys(keys),
            indentSize: 30,
        },
    };

    return (
        <div className="lead-sources-data-table-container">
            <div className="table-controls">
                <div className="pipeline-filter" style={{ width: "170px" }}>
                    <CommonSelect
                        className="select small text-muteds"
                        options={viewOptions}
                        defaultValue={selectedOption}
                        onChange={(selected: any) => {
                            setSelectedView(selected.value);
                            setExpandedRowKeys([]);
                        }}
                        placeHolder="Filter By Form"
                    />
                </div>

                {showSearch && (
                    <div className="search-wrapper">
                        <Input
                            style={{ width: "140px" }}
                            className="p-2"
                            placeholder="Search sources..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => handleSearch(e.target.value)}
                            allowClear
                        />
                    </div>
                )}

                <button
                    className="btn btn-outline-secondary btn-md expand-collapse-btn"
                    onClick={() => {
                        if (expandedRowKeys.length > 0) {
                            setExpandedRowKeys([]);
                        } else {
                            const getAllKeys = (dataArray: SourceStageData[]): string[] => {
                                const keys: string[] = [];
                                dataArray.forEach((item) => {
                                    if (item.children && item.children.length > 0) {
                                        keys.push(item.key!);
                                        keys.push(...getAllKeys(item.children));
                                    }
                                });
                                return keys;
                            };
                            setExpandedRowKeys(getAllKeys(filteredData));
                        }
                    }}
                >
                    {expandedRowKeys.length > 0 ? "Collapse" : "Expand"}
                </button>
            </div>

            <Table {...tableProps} />
        </div>
    );
};

export default SourcesStageWiseDataTable;
